/**
 * Simple caching utility với TTL (Time To Live)
 * Sử dụng localStorage để persist cache across sessions
 */

const CACHE_PREFIX = 'chamcong_cache_';
const CACHE_TIMESTAMP_PREFIX = 'chamcong_cache_ts_';

/**
 * Lấy data từ cache nếu còn valid
 * @param {string} key - Cache key
 * @param {number} ttl - Time to live (milliseconds)
 * @returns {any|null} - Cached data hoặc null nếu expired
 */
export const getCached = (key, ttl) => {
  try {
    const cacheKey = CACHE_PREFIX + key;
    const timestampKey = CACHE_TIMESTAMP_PREFIX + key;
    
    const cachedData = localStorage.getItem(cacheKey);
    const cachedTimestamp = localStorage.getItem(timestampKey);
    
    if (!cachedData || !cachedTimestamp) {
      return null;
    }
    
    const now = Date.now();
    const timestamp = parseInt(cachedTimestamp, 10);
    const age = now - timestamp;
    
    if (age > ttl) {
      // Cache expired, remove it
      localStorage.removeItem(cacheKey);
      localStorage.removeItem(timestampKey);
      return null;
    }
    
    // Cache still valid
    return JSON.parse(cachedData);
  } catch (error) {
    console.error('Error reading cache:', error);
    return null;
  }
};

/**
 * Lưu data vào cache
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 */
export const setCached = (key, data) => {
  const cacheKey = CACHE_PREFIX + key;
  const timestampKey = CACHE_TIMESTAMP_PREFIX + key;
  
  try {
    localStorage.setItem(cacheKey, JSON.stringify(data));
    localStorage.setItem(timestampKey, Date.now().toString());
  } catch (error) {
    console.error('Error writing cache:', error);
    // localStorage might be full, try to clear old cache
    clearOldCache();
    try {
      localStorage.setItem(cacheKey, JSON.stringify(data));
      localStorage.setItem(timestampKey, Date.now().toString());
    } catch (e) {
      console.error('Failed to write cache after cleanup:', e);
    }
  }
};

/**
 * Xóa cache cho một key cụ thể
 * @param {string} key - Cache key
 */
export const clearCache = (key) => {
  try {
    const cacheKey = CACHE_PREFIX + key;
    const timestampKey = CACHE_TIMESTAMP_PREFIX + key;
    
    localStorage.removeItem(cacheKey);
    localStorage.removeItem(timestampKey);
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
};

/**
 * Xóa tất cả cache cũ (older than 24 hours)
 */
export const clearOldCache = () => {
  try {
    const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);
    const keys = Object.keys(localStorage);
    
    keys.forEach(key => {
      if (key.startsWith(CACHE_TIMESTAMP_PREFIX)) {
        const timestamp = parseInt(localStorage.getItem(key), 10);
        if (timestamp && timestamp < oneDayAgo) {
          // Remove both cache and timestamp
          const cacheKey = key.replace(CACHE_TIMESTAMP_PREFIX, CACHE_PREFIX);
          localStorage.removeItem(cacheKey);
          localStorage.removeItem(key);
        }
      }
    });
  } catch (error) {
    console.error('Error clearing old cache:', error);
  }
};

/**
 * Xóa tất cả cache
 */
export const clearAllCache = () => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_PREFIX) || key.startsWith(CACHE_TIMESTAMP_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error('Error clearing all cache:', error);
  }
};

/**
 * Cache TTL constants (milliseconds)
 */
export const CACHE_TTL = {
  EMPLOYEES: 5 * 60 * 1000,        // 5 phút
  PROJECT_LOCATIONS: 30 * 60 * 1000, // 30 phút
  LOCATION: 5 * 60 * 1000,          // 5 phút
  TODAY_ATTENDANCE: 60 * 1000,      // 1 phút
  ATTENDANCE_HISTORY: 5 * 60 * 1000, // 5 phút
};
